import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import {
  listSchedules,
  createSchedule,
  pauseSchedule,
  resumeSchedule,
  deleteSchedule,
} from "../services/schedulerService";

const OPS = ["bulk_update", "bulk_delete", "export", "archive", "backup", "cleanup"];
const FREQ = ["hourly", "daily", "weekly", "monthly", "custom"];

export default function SchedulerPage() {
  const { allCollections, user } = useFirestoreAdmin();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    name: "",
    operation: "backup",
    collection: "",
    frequency: "daily",
    cron: "",
  });

  const load = async () => {
    try {
      setRows(await listSchedules());
    } catch (e) {
      toast.error(e?.message || "Could not load schedules");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await createSchedule({
        ...form,
        createdBy: user?.email,
      });
      toast.success("Schedule created");
      setForm({ name: "", operation: "backup", collection: "", frequency: "daily", cron: "" });
      load();
    } catch (e) {
      toast.error(e?.message || "Create failed");
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Scheduler</h1>
        <p className="text-sm text-slate-600">Schedule bulk operations. Stored in Firestore; use Cloud Functions for automatic runs.</p>
      </header>
      <div className="fa-card grid gap-3 p-4 sm:grid-cols-2">
        <input className="rounded-lg border px-2 py-1.5 text-sm" placeholder="Schedule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select className="rounded-lg border px-2 py-1.5 text-sm" value={form.operation} onChange={(e) => setForm({ ...form, operation: e.target.value })}>
          {OPS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="rounded-lg border px-2 py-1.5 text-sm" value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })}>
          <option value="">Collection (optional)</option>
          {allCollections.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="rounded-lg border px-2 py-1.5 text-sm" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
          {FREQ.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        {form.frequency === "custom" && (
          <input className="rounded-lg border px-2 py-1.5 text-sm sm:col-span-2" placeholder="Cron expression" value={form.cron} onChange={(e) => setForm({ ...form, cron: e.target.value })} />
        )}
        <button type="button" className="fa-btn fa-btn-primary" onClick={create}>Create schedule</button>
      </div>
      <div className="fa-card fa-table-wrap">
        <table className="fa-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Operation</th>
              <th>Collection</th>
              <th>Frequency</th>
              <th>Next run</th>
              <th>Last run</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>{r.operation}</td>
                <td>{r.collection || "—"}</td>
                <td>{r.frequency}</td>
                <td className="text-xs">{r.nextRun || "—"}</td>
                <td className="text-xs">{r.lastRun || "—"}</td>
                <td>{r.status}</td>
                <td className="space-x-1">
                  {r.status === "active" ? (
                    <button type="button" className="fa-btn text-xs" onClick={() => pauseSchedule(r.id).then(load)}>Pause</button>
                  ) : (
                    <button type="button" className="fa-btn text-xs" onClick={() => resumeSchedule(r.id, r).then(load)}>Resume</button>
                  )}
                  <button type="button" className="fa-btn text-xs" onClick={() => deleteSchedule(r.id).then(load)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
